import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models/routing.types';
import type {
  RouteMonitoringVisit,
  RouteMonitoringVisitShipping,
} from '../models';

export const UNKNOWN_STATUS_LABEL = 'Sin estado';
export const UNKNOWN_SERVICE_LABEL = 'Sin servicio';

export interface RouteShippingFacet {
  number: number;
  statusLabel: string | null;
  serviceLabel: string;
}

export interface RouteShippingFilterOption {
  value: string;
  label: string;
  count: number;
}

const hasShippingNumber = (
  shipping: RouteMonitoringVisitShipping | undefined
): shipping is RouteMonitoringVisitShipping & { number: number } =>
  shipping?.number != null && shipping.number > 0;

export const getVisitShippingNumbers = (
  visit: RouteMonitoringVisit
): number[] => {
  const numbers = (visit.shippings ?? [])
    .filter(hasShippingNumber)
    .map((shipping) => shipping.number);
  if (numbers.length > 0) return numbers;
  return visit.shippingNumber > 0 ? [visit.shippingNumber] : [];
};

const getRouteServiceLabel = (
  shipping: RouteMonitoringVisitShipping | undefined
): string | null =>
  shipping?.serviceName?.trim() ||
  shipping?.commercialService?.name?.trim() ||
  shipping?.serviceWithOperator?.commercialService?.name?.trim() ||
  null;

const getEnrichedServiceLabel = (
  shipping: ShippingForRouting | undefined
): string | null =>
  shipping?.serviceWithOperator?.commercialService?.name?.trim() ||
  shipping?.commercialService?.name?.trim() ||
  null;

const getStatusLabel = (
  shipping: ShippingForRouting | undefined
): string | null =>
  shipping?.status?.commercialName?.trim() ||
  shipping?.status?.name?.trim() ||
  null;

/*
 * Un pedido por número aunque aparezca en más de una visita. El servicio sale
 * del detalle de la ruta (llega enseguida) y el estado, del pedido real
 * (`GET /shipping/{number}`): el del vínculo con la visita sigue en
 * "Pendiente" cuando al pedido lo anulan por una transición.
 */
export const buildRouteShippingFacets = (
  visits: RouteMonitoringVisit[],
  enrichedByNumber: Map<number, ShippingForRouting>
): RouteShippingFacet[] => {
  const facetsByNumber = new Map<number, RouteShippingFacet>();

  const addFacet = (
    number: number,
    routeShipping: RouteMonitoringVisitShipping | undefined
  ) => {
    if (facetsByNumber.has(number)) return;
    const enriched = enrichedByNumber.get(number);
    facetsByNumber.set(number, {
      number,
      statusLabel: getStatusLabel(enriched),
      serviceLabel:
        getRouteServiceLabel(routeShipping) ??
        getEnrichedServiceLabel(enriched) ??
        UNKNOWN_SERVICE_LABEL,
    });
  };

  visits.forEach((visit) => {
    const shippings = (visit.shippings ?? []).filter(hasShippingNumber);
    if (shippings.length === 0) {
      if (visit.shippingNumber > 0) addFacet(visit.shippingNumber, undefined);
      return;
    }
    shippings.forEach((shipping) => addFacet(shipping.number, shipping));
  });

  return [...facetsByNumber.values()];
};

export const countFacetOptions = (
  facets: RouteShippingFacet[],
  getValue: (facet: RouteShippingFacet) => string,
  getLabel: (value: string) => string,
  selectedValue: string | null
): RouteShippingFilterOption[] => {
  const counts = new Map<string, number>();
  facets.forEach((facet) => {
    const value = getValue(facet);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  if (selectedValue !== null && !counts.has(selectedValue)) {
    counts.set(selectedValue, 0);
  }

  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: getLabel(value), count }))
    .sort((first, second) =>
      first.label.localeCompare(second.label, 'es', { sensitivity: 'base' })
    );
};
